import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';

interface FileLinkClickPayload {
  href: string;
  fileId?: string;
  fileUrl?: string;
  label: string;
}

interface MarkdownRendererProps {
  content: string;
  onFileLinkClick?: (payload: FileLinkClickPayload) => void;
}

const StyledMarkdown = styled.div`
  font-family: Poppins, sans-serif;
  font-size: 13px;
  line-height: 1.6;
  color: #2a3039;

  /* Headers */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: bold;
    margin-top: 16px;
    margin-bottom: 10px;
    color: #2a3039;
  }

  h1 {
    font-size: 20px;
  }

  h2 {
    font-size: 16px;
  }

  h3 {
    font-size: 14px;
  }

  /* Paragraphs */
  p {
    margin: 8px 0;
    line-height: 1.6;
  }

  /* Lists */
  ul,
  ol {
    margin: 8px 0;
    padding-left: 24px;
  }

  li {
    margin: 4px 0;
    line-height: 1.6;
  }

  /* Links */
  a {
    color: #2d70e2;
    text-decoration: underline;
    cursor: pointer;
  }

  a:hover {
    color: #1e5bb8;
  }

  /* Bold text */
  strong {
    font-weight: bold;
  }

  /* Code blocks */
  code {
    background-color: #f4f4f4;
    padding: 2px 4px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 13px;
  }

  pre {
    background-color: #f4f4f4;
    padding: 12px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 8px 0;
  }

  pre code {
    background-color: transparent;
    padding: 0;
  }
`;

const getFileIdFromHref = (href: string): string | undefined => {
  try {
    const url = new URL(href, window.location.origin);
    const match = url.pathname.match(/repository-files\/([^\/]+)(?:\/stream)?\/?$/);
    return match?.[1];
  } catch {
    const match = href.match(/repository-files\/([^\/]+)(?:\/stream)?\/?$/);
    return match?.[1];
  }
};

const getLinkText = (children: React.ReactNode): string => {
  if (typeof children === 'string') {
    return children;
  }
  if (Array.isArray(children)) {
    return children.map(getLinkText).join('');
  }
  if (React.isValidElement(children)) {
    return getLinkText(children.props.children);
  }
  return '';
};

const isPreviewableHref = (href: string): boolean => {
  const normalizedHref = href.split('?')[0].split('#')[0];
  const extension = normalizedHref.split('.').pop()?.toLowerCase();
  if (extension === 'pdf' || extension === 'mp4') {
    return true;
  }
  return Boolean(getFileIdFromHref(href));
};

const createMarkdownComponents = (onFileLinkClick?: (payload: FileLinkClickPayload) => void): Partial<Components> => ({
  a: ({ node, href, children, ...props }) => {
    const linkText = getLinkText(children);
    const fileId = href ? getFileIdFromHref(href) : undefined;
    const previewable = href ? isPreviewableHref(href) : false;

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => {
          if (!href) {
            return;
          }

          if (previewable && onFileLinkClick) {
            e.preventDefault();
            e.stopPropagation();
            onFileLinkClick({ href, fileId, fileUrl: href, label: linkText || href });
            return;
          }

          e.preventDefault();
          const url = href.startsWith('http') ? href : new URL(href, window.location.origin).toString();
          window.open(url, '_blank', 'noopener,noreferrer');
        }}
        {...props}
      >
        {children}
      </a>
    );
  },
});

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, onFileLinkClick }) => {
  // Handle empty or whitespace-only content
  if (!content || !content.trim()) {
    return null;
  }

  return (
    <StyledMarkdown>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={createMarkdownComponents(onFileLinkClick)}>
        {content}
      </ReactMarkdown>
    </StyledMarkdown>
  );
};
